import apiService from './apiService';
import { AxiosRequestConfig } from 'axios';

export interface Segment {
  id: number;
  name: string;
  description: string | null;
}

export interface Contact {
  id: number;
  client_id: number;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  nick_name: string | null;
  gender: string | null;
  birth_date: string | null;
  email_address: string | null;
  mobile_phone: string | null;
  phone: string | null;
  primary_agent: string | null;
  title: string | null;
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip_code: string | null;
  country: string | null;
  dnc_email: boolean;
  dnc_email_note: string | null;
  dnc_email_date: string | null;
  dnc_sms: boolean;
  dnc_sms_note: string | null;
  dnc_sms_date: string | null;
  segments: Array<{id: number; name: string}>;
  vcrm_profile_id?: number | null;
  vcrm_primary_agent?: string;
  vcrm_sync_date?: string | null;
  vcrm_active?: boolean;
  is_deleted?: boolean;
  created_at?: string;
  updated_at?: string;
}

interface ContactsResponse {
  contacts: Contact[];
  total_count: number;
}

class ContactService {
  async getContacts(params: Record<string, any>, options?: AxiosRequestConfig): Promise<ContactsResponse> {
    try {
      const response = await apiService.get<ContactsResponse>('/api/contacts', {
        params,
        ...options
      });
      return response.data;
    } catch (error) {
      console.error('ContactService - Error fetching contacts:', error);
      throw error;
    }
  }

  async getSegments(client_id: number, config?: AxiosRequestConfig): Promise<Segment[]> {
    const maxRetries = 2;
    let retryCount = 0;

    while (retryCount <= maxRetries) {
      try {
        const response = await apiService.get<Segment[]>('/api/segments', { 
          params: { client_id },
          ...config,
          timeout: 10000 // Reduce timeout to 10 seconds
        });
        return response.data.filter(seg => {
          const name = seg.name.toLowerCase();
          return name !== 'unsubscribed' && name !== 'all contacts';
        });
      } catch (error) {
        // Check if this is a cancellation or network error
        if (error instanceof Error) {
          if (error.name === 'CanceledError' || error.name === 'AbortError') {
            console.log('Request cancelled:', error.message);
            throw error;
          }
          if (error.message.includes('Network Error') || error.message.includes('timeout')) {
            if (retryCount < maxRetries) {
              console.log(`Retrying segments request (attempt ${retryCount + 1})`);
              retryCount++;
              // Wait before retrying (exponential backoff)
              await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retryCount)));
              continue;
            }
            console.error('Network or timeout error after retries:', error.message);
            throw new Error('Failed to connect to the server. Please check your connection and try again.');
          }
        }
        console.error('ContactService - Error fetching segments:', error);
        throw error;
      }
    }
    throw new Error('Failed to fetch segments after maximum retries');
  }

  async getContact(contactId: string | number, config?: AxiosRequestConfig): Promise<Contact> {
    try {
      const response = await apiService.get<Contact>(`/api/contacts/contacts/${contactId}`, config);
      return response.data;
    } catch (error) {
      console.error('ContactService - Error fetching contact:', error);
      throw error;
    }
  }

  async updateContact(contactId: string | number, data: Partial<Contact>, config?: AxiosRequestConfig): Promise<Contact> {
    try {
      const response = await apiService.patch<Contact>(`/api/contacts/contacts/${contactId}`, data, config);
      return response.data;
    } catch (error) {
      console.error('ContactService - Error updating contact:', error);
      throw error;
    }
  }

  async getContactEmailEngagement(contactId: string | number, clientId: number, includeSpam?: boolean, startDate?: string, endDate?: string, config?: AxiosRequestConfig): Promise<any> {
    try {
      const params: any = { client_id: clientId };
      if (includeSpam !== undefined) {
        params.include_spam = includeSpam;
      }
      if (startDate) {
        params.start_date = startDate;
      }
      if (endDate) {
        params.end_date = endDate;
      }
      
      console.log('ContactService - Email engagement params:', params);
      
      // Use the new comprehensive analytics endpoint for consistency
      const response = await apiService.get<any>(`/api/email-analytics/contact-engagement/${contactId}`, {
        params,
        ...config
      });
      return response.data;
    } catch (error) {
      console.error('ContactService - Error fetching contact email engagement:', error);
      throw error;
    }
  }

  async createContact(data: Partial<Contact>, options?: { segment_ids?: number[] }): Promise<Contact> {
    try {
      console.log('ContactService - Sending contact data:', JSON.stringify(data, null, 2));
      console.log('ContactService - Sending segment_ids:', options?.segment_ids);
      
      // Create request body with segment_ids as a separate field
      const requestBody: any = { ...data };
      if (options?.segment_ids && options.segment_ids.length > 0) {
        requestBody.segment_ids = options.segment_ids;
      }
      
      console.log('ContactService - Final request body:', JSON.stringify(requestBody, null, 2));
      const response = await apiService.post<Contact>('/api/contacts/create', requestBody);
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error creating contact:', error);
      console.error('ContactService - Error response data:', error?.response?.data);
      console.error('ContactService - Error response status:', error?.response?.status);
      throw error;
    }
  }

  async importInstantlyAiCsv(file: File, clientId: number, testMode: boolean = false): Promise<{
    success: boolean;
    tag_name: string;
    tag_id?: number;
    job_id?: string;
    test_result?: any;
    message?: string;
  }> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const timeout = testMode ? 60000 : 300000; // 60s for test, 5min for full import
      
      const response = await apiService.post<{
        success: boolean;
        tag_name: string;
        tag_id?: number;
        job_id?: string;
        test_result?: any;
        message?: string;
      }>(`/api/contacts/import-instantly-ai?client_id=${clientId}&test_mode=${testMode}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout,
      });
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error importing CSV:', error);
      throw error;
    }
  }

  async checkImportProgress(jobId: string, clientId: number): Promise<ImportContactJob> {
    try {
      const response = await apiService.get<ImportContactJob>(
        `/api/contacts/import-jobs/${jobId}/progress?client_id=${clientId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error checking import progress:', error);
      throw error;
    }
  }

  async getImportJobs(clientId: number, status?: string, skip: number = 0, limit: number = 50): Promise<ImportContactJob[]> {
    try {
      const params: any = { client_id: clientId, skip, limit };
      if (status) {
        params.status = status;
      }
      const response = await apiService.get<ImportContactJob[]>('/api/contacts/import-jobs', { params });
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error fetching import jobs:', error);
      throw error;
    }
  }

  async getImportJob(jobId: number, clientId: number): Promise<ImportContactJob> {
    try {
      const response = await apiService.get<ImportContactJob>(
        `/api/contacts/import-jobs/${jobId}?client_id=${clientId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error fetching import job:', error);
      throw error;
    }
  }

  async stopImportJob(jobId: number, clientId: number): Promise<{ success: boolean; message: string; job_id: string; status: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string; job_id: string; status: string }>(
        `/api/contacts/import-jobs/${jobId}/stop?client_id=${clientId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error stopping import job:', error);
      throw error;
    }
  }

  async restartImportJob(jobId: number, clientId: number): Promise<{ success: boolean; message: string; job_id: string; status: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string; job_id: string; status: string }>(
        `/api/contacts/import-jobs/${jobId}/restart?client_id=${clientId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error restarting import job:', error);
      throw error;
    }
  }

  async retryImportJob(jobId: number, clientId: number): Promise<{ success: boolean; message: string; job_id: string; status: string }> {
    try {
      const response = await apiService.post<{ success: boolean; message: string; job_id: string; status: string }>(
        `/api/contacts/import-jobs/${jobId}/retry?client_id=${clientId}`
      );
      return response.data;
    } catch (error: any) {
      console.error('ContactService - Error retrying import job:', error);
      throw error;
    }
  }

  async getImportJobFileUrl(jobId: number, clientId: number): Promise<{ url: string; filename: string }> {
    try {
      const response = await apiService.get<{ url: string; filename: string }>(
        `/api/contacts/import-jobs/${jobId}/file?client_id=${clientId}&view=false`
      );
      return response.data;
    } catch (error: any) {
      console.error('Error getting import job file URL:', error);
      throw error;
    }
  }

  async getImportJobFileContent(jobId: number, clientId: number): Promise<string> {
    try {
      const response = await apiService.get<string>(
        `/api/contacts/import-jobs/${jobId}/file?client_id=${clientId}&view=true`,
        { responseType: 'text' }
      );
      return response.data as string;
    } catch (error: any) {
      console.error('Error getting import job file content:', error);
      throw error;
    }
  }

  async downloadImportTemplate(): Promise<Blob> {
    try {
      const response = await apiService.get<Blob>('/api/contacts/import-instantly-ai/template', {
        responseType: 'blob'
      });
      return response.data as Blob;
    } catch (error: any) {
      console.error('ContactService - Error downloading template:', error);
      throw error;
    }
  }
}

export interface ImportContactJob {
  id: number;
  client_id: number;
  filename: string;
  s3_key?: string;
  tag_name: string;
  tag_id?: number;
  status: string;
  total_rows: number;
  processed_rows: number;
  created_count: number;
  updated_count: number;
  skipped_count: number;
  error_message?: string;
  file_hash?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  completed_at?: string;
}

export default new ContactService(); 